<template>
  <div class="data-table-condensed">
    <q-table
      class="data-table-condensed__table scroll"
      title-class="data-table-condensed__table--title"
      dense
      virtual-scroll
      flat
      :columns="columns"
      :filter="search"
      :pagination.sync="pagination"
      :data="rows"
      no-data-label="No Data"
      no-results-label="The filter didn't uncover any results"
      color="primary"
      row-key="name"
      style="height: 400px;"
      :loading="visible"
      ><template v-slot:loading>
        <DataLoader label="Loading Filter Data" />
      </template>
      <template v-slot:top-left>
        <q-input
          rounded
          outlined
          dense
          color="secondary"
          debounce="300"
          v-model="search"
          placeholder="Search"
        >
          <template v-slot:append>
            <q-icon name="search" />
          </template>
        </q-input>
      </template>
      <template v-slot:top-right="props">
        <q-btn
          color="secondary"
          icon-right="archive"
          label="Download"
          no-caps
          unelevated
          rounded
          :disable="rows.length === 0"
          @click="exportTable"
        />
        <q-btn
          round
          unelevated
          outline
          dense
          class="q-ml-sm"
          color="secondary"
          :icon="props.inFullscreen ? 'fullscreen_exit' : 'fullscreen'"
          @click="props.toggleFullscreen"
        />
      </template>
    </q-table>
  </div>
</template>

<script>
import { exportFile } from "quasar";
import DataLoader from "@/components/other/DataLoader.vue";
import { count } from "@/utils/formatNumbers";
import { formatCurrency } from "@/utils/formatCurrency";
import { formatDateTime } from "@/utils/formatDate";
import { formatTextLabel } from "@/utils/formatText";

function wrapCsvValue(val, formatFn) {
  let formatted = formatFn !== void 0 ? formatFn(val) : val;
  formatted =
    formatted === void 0 || formatted === null ? "" : String(formatted);
  formatted = formatted.split('"').join('""');
  /**
   * Excel accepts \n and \r in strings, but some other CSV parsers do not
   * Uncomment the next two lines to escape new lines
   */
  // .split('\n').join('\\n')
  // .split('\r').join('\\r')
  return `"${formatted}"`;
}

export default {
  props: {
    dataset: String,
    timer: Number,
    filter: Object,
  },
  data() {
    return {
      pagination: {
        page: 1,
        rowsPerPage: 20,
      },
      columns: [],
      keys: [],
      rows: [],
      errors: [],
      interval: null,
      dataRefresh: this.$props.timer,
      visible: false,
      search: "",
      tableFilter: "",
    };
  },
  components: {
    DataLoader,
  },
  computed: {
    numberOfPages() {
      return Math.ceil(this.rows.length / this.pagination.rowsPerPage);
    },
    updateTableFilter() {
      return this.$store.getters.GET_LIVE_ACQUISITION_FILTER;
    },
  },
  methods: {
    async fetchData(siteId, networkId, operatorId) {
      // Dataset name is the store procedure name used to return the results.
      const payload = {
        filterOptions: {
          siteIds: siteId ? [siteId] : [],
          networkId,
          operatorId,
          filter: this.tableFilter,
        },
        report: "live-acquisition",
        dataset: this.$props.dataset.replace(/ /g, ""),
      };

      await this.$store
        .dispatch("fetchData", payload)
        .then((response) => {
          if (response.data[0] !== undefined) {
            const keys = Object.keys(response.data[0]);

            this.columns = keys.map((key) => ({
              name: key,
              label: formatTextLabel(key),
              field: key,
              sortable: true,
              align: "left",
              format: (value) => {
                if (
                  (key === "RegistrationDatetime" && value !== null) ||
                  (key === "FirstRegistrationDate" && value !== null)
                ) {
                  return formatDateTime(value);
                } else if (
                  (key === "LifetimeAccounts" && value !== null) ||
                  (key === "TodayAccounts" && value !== null)
                ) {
                  return count(value);
                } else if (
                  (key === "FirstDeposit" && value !== null) ||
                  (key === "DayDeposits" && value !== null)
                ) {
                  return formatCurrency(value);
                } else if (key === "DayGGR" && value !== null) {
                  return formatCurrency(value);
                } else if (value !== null) {
                  return value;
                } else {
                  return `---`;
                }
              },
            }));
          }
          this.rows = response.data;
          this.visible = false;
        })
        .catch((e) => {
          this.errors.push(e);
        });
    },

    exportTable() {
      // naive encoding to csv format
      const content = [this.columns.map((col) => wrapCsvValue(col.label))]
        .concat(
          this.rows.map((row) =>
            this.columns
              .map((col) =>
                wrapCsvValue(
                  typeof col.field === "function"
                    ? col.field(row)
                    : row[col.field === void 0 ? col.name : col.field],
                  col.format
                )
              )
              .join(",")
          )
        )
        .join("\r\n");

      const BOM = "\uFEFF";
      const status = exportFile(
        "table-export.csv",
        BOM + content,
        "text/csv;charset=utf-8"
      );

      if (status !== true) {
        this.$q.notify({
          message: "Browser denied file download...",
          color: "negative",
          icon: "warning",
        });
      }
    },
  },
  watch: {
    filter: function(filterOption) {
      if (this.tableFilter) {
        this.fetchData(
          filterOption.siteId,
          filterOption.networkId,
          filterOption.operatorId
        );
        this.visible = true;

        clearInterval(this.interval);
        this.interval = setInterval(() => {
          this.fetchData(
            filterOption.siteId,
            filterOption.networkId,
            filterOption.operatorId
          );
        }, 180000);
      }
    },
    // May need a better solution
    updateTableFilter(selectedFilter) {
      this.visible = true;
      this.tableFilter = selectedFilter;

      // If value contains channel return channel only
      this.fetchData(
        this.$store.state.siteFilter.id,
        this.$store.state.networkFilter.id,
        this.$store.state.selectedOperator.id
      );
      clearInterval(this.interval);
      this.interval = setInterval(() => {
        this.fetchData(
          this.$store.state.siteFilter.id,
          this.$store.state.networkFilter.id,
          this.$store.state.selectedOperator.id
        );
      }, this.dataRefresh);
    },
  },

  beforeDestroy: function() {
    clearInterval(this.interval);
  },
};
</script>
